import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useInView, useReducedMotion } from 'framer-motion';
import { ArrowRight, ChevronDown, Phone, Check, Clock, MessageSquare, CalendarCheck, Search, MapPin, Star } from 'lucide-react';
import Reveal from '@/components/Reveal';
import { GROWTH_STAGES as STAGES } from '@/lib/site';

/**
 * BUILD → ATTRACT → CONVERT → QUALIFY → FOLLOW UP → GROW.
 *
 * The six stages of the HyenaMind system. Each stage draws its own diagram from
 * real DOM elements rather than SVG with hand-placed <text>: coordinate-placed
 * text cannot reflow, so labels collided at some widths and could not inherit
 * the brand fonts. The browser does the layout here, which makes overlap
 * structurally impossible and lets desktop and mobile be composed separately.
 *
 * No photography and no numbers. The stock available for these stages was a
 * generic laptop desk shot and a screenshot of an unrelated SaaS dashboard full
 * of invented metrics; nothing here carries a figure that could not be verified.
 */

/* ------------------------------------------------------------- primitives */

const Surface = ({ children, className = '', accent = false }) => (
  <div
    className={`relative rounded-md border bg-white/[0.03] ${
      accent ? 'border-steel-light/40' : 'border-white/[0.10]'
    } ${className}`}
  >
    {children}
  </div>
);

const Step = ({ children, rank = 'quiet', className = '' }) => {
  const styles = {
    loud: 'border-white/20 bg-white/[0.05] text-bone',
    quiet: 'border-white/[0.09] bg-white/[0.02] text-silver',
    ghost: 'border-white/[0.06] bg-transparent text-silver/65',
  };
  return (
    <div className={`rounded-md border px-3.5 py-2.5 text-[12.5px] leading-tight ${styles[rank]} ${className}`}>
      {children}
    </div>
  );
};

const Outcome = ({ title, meta }) => (
  <div className="rounded-md bg-bone px-3.5 py-3 shadow-[0_10px_28px_-12px_rgba(5,5,7,0.8)]">
    <p className="font-display text-[15px] font-semibold leading-none tracking-tight text-navy">{title}</p>
    {meta && (
      <div className="mt-2 flex flex-wrap gap-x-3.5 gap-y-1">
        {meta.map((m) => (
          <span key={m} className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-navy/60">
            {m}
          </span>
        ))}
      </div>
    )}
  </div>
);

const Flow = ({ label }) => (
  <div className="flex flex-col items-center py-1.5" aria-hidden="true">
    <span className="h-3 w-px bg-white/20" />
    {label && <span className="py-1 text-[9px] font-bold uppercase tracking-[0.16em] text-silver/80">{label}</span>}
    <ChevronDown className="h-3 w-3 text-white/25" strokeWidth={2.5} />
  </div>
);

const Browser = ({ children, url }) => (
  <Surface className="overflow-hidden">
    <div className="flex items-center gap-1.5 border-b border-white/[0.09] px-3 py-2">
      <span className="h-[5px] w-[5px] rounded-full bg-white/20" aria-hidden="true" />
      <span className="h-[5px] w-[5px] rounded-full bg-white/20" aria-hidden="true" />
      <span className="h-[5px] w-[5px] rounded-full bg-white/20" aria-hidden="true" />
      {url && (
        <span className="ml-2 flex-1 truncate rounded-full bg-black/25 px-2.5 py-1 font-mono text-[9.5px] text-silver/65">
          {url}
        </span>
      )}
    </div>
    <div className="p-3">{children}</div>
  </Surface>
);

const Page = ({ cta = 'Request a quote' }) => (
  <>
    <div className="h-9 rounded-md bg-white/[0.09]" aria-hidden="true" />
    <div className="mt-1.5 flex gap-1.5" aria-hidden="true">
      <span className="h-3.5 flex-1 rounded-[3px] bg-white/[0.06]" />
      <span className="h-3.5 flex-1 rounded-[3px] bg-white/[0.06]" />
      <span className="h-3.5 flex-1 rounded-[3px] bg-white/[0.06]" />
    </div>
    <div className="mt-2 rounded-md bg-bone py-1.5 text-center text-[10.5px] font-bold tracking-wide text-navy">{cta}</div>
  </>
);

const Stage = ({ children, label, caption }) => (
  <figure className="m-0 flex h-full w-full flex-col justify-center" role="img" aria-label={label}>
    {children}
    {caption && (
      <figcaption className="mt-5 text-[9.5px] font-bold uppercase tracking-[0.2em] text-silver">
        {caption}
      </figcaption>
    )}
  </figure>
);

/* ------------------------------------------------------------- 01 BUILD */

function BuildVisual() {
  return (
    <Stage
      label="Diagram: a local business website structured around an enquiry, with a matching mobile layout"
      caption="Trust · speed · conversion · tracking"
    >
      <div className="flex gap-4">
        <div className="flex-1">
          <Browser url="yourcompany.com">
            <div className="space-y-1.5">
              <Step rank="loud">Hero + request a quote</Step>
              <Step>Services</Step>
              <Step rank="ghost">Recent work</Step>
              <div className="rounded-md bg-bone px-3.5 py-2 text-[12px] font-bold text-navy">Enquiry form</div>
            </div>
          </Browser>
        </div>
        <div className="w-[4.25rem] shrink-0">
          <Surface className="rounded-[1.1rem] p-[5px]">
            <span className="mx-auto mb-1 block h-[3px] w-5 rounded-full bg-white/20" aria-hidden="true" />
            <div className="overflow-hidden rounded-[0.8rem] bg-white/[0.04] p-1.5">
              <div className="h-9 rounded-[3px] bg-white/[0.09]" aria-hidden="true" />
              <div className="mt-1 h-2 rounded-[2px] bg-white/[0.06]" aria-hidden="true" />
              <div className="mt-1 h-2 w-2/3 rounded-[2px] bg-white/[0.06]" aria-hidden="true" />
              <div className="mt-1.5 flex items-center justify-center gap-1 rounded-[3px] bg-bone py-1 text-[7.5px] font-bold text-navy">
                <Phone className="h-2 w-2" strokeWidth={3} aria-hidden="true" />
                Call
              </div>
            </div>
          </Surface>
          <p className="mt-2 text-center text-[9.5px] leading-tight text-silver/70">Every screen</p>
        </div>
      </div>
    </Stage>
  );
}

/* -------------------------------------------------------- 02 GET FOUND */

function GetFoundVisual() {
  return (
    <Stage
      label="Diagram: a local search showing a map result and business profile, leading to a call or directions"
      caption="Profile → local pack → contact"
    >
      <div className="mx-auto w-full max-w-md">
        <Surface className="flex items-center gap-2.5 rounded-full px-4 py-2.5">
          <Search className="h-3.5 w-3.5 shrink-0 text-steel-light" strokeWidth={2.25} aria-hidden="true" />
          <span className="text-[13px] text-bone">roof repair near me</span>
        </Surface>

        <Flow label="Local results" />

        <div className="flex gap-3">
          <Surface className="relative w-[4.5rem] shrink-0 overflow-hidden" aria-hidden="true">
            <div className="grid h-full grid-cols-3 grid-rows-4 opacity-25">
              {Array.from({ length: 12 }).map((_, i) => (
                <span key={i} className="border-b border-r border-white/25" />
              ))}
            </div>
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <MapPin className="h-5 w-5 text-steel-light" fill="currentColor" strokeWidth={1.5} />
            </span>
          </Surface>

          <Surface accent className="flex-1 px-3.5 py-3">
            <p className="font-display text-[14px] font-medium tracking-tight text-bone">Your business</p>
            <p className="mt-1 flex items-center gap-1.5 text-[11px] text-silver/65">
              <Star className="h-2.5 w-2.5 text-steel-light" fill="currentColor" aria-hidden="true" />
              Reviews · Hours · Service area
            </p>
            <div className="mt-2.5 flex gap-1.5">
              <span className="rounded-[4px] bg-bone px-2.5 py-1 text-[9.5px] font-bold text-navy">Call</span>
              <span className="rounded-[4px] border border-white/20 px-2.5 py-1 text-[9.5px] font-bold text-silver">
                Directions
              </span>
            </div>
          </Surface>
        </div>

        <div className="mt-2 space-y-1.5">
          <Step rank="ghost">Another local business</Step>
          <Step rank="ghost">Another local business</Step>
        </div>
      </div>
    </Stage>
  );
}

/* ----------------------------------------------------------- 03 ATTRACT */

function AttractVisual() {
  const channels = ['Google Ads', 'Meta Ads', 'TikTok Ads', 'Social & retargeting'];
  return (
    <Stage
      label="Diagram: paid media channels all directing traffic to one website"
      caption="Paid channels · one destination"
    >
      <div className="flex flex-col gap-5 lg:flex-row lg:items-stretch lg:gap-0">
        <ul className="flex flex-col gap-2 lg:w-[12rem] lg:shrink-0">
          {channels.map((c) => (
            <li key={c}>
              <Step>{c}</Step>
            </li>
          ))}
        </ul>

        {/* Connector spine drawn with borders, so nothing can overlap a label. */}
        <div className="relative hidden w-20 shrink-0 lg:block" aria-hidden="true">
          <div className="absolute left-0 top-[12.5%] h-px w-7 bg-white/25" />
          <div className="absolute left-0 top-[37.5%] h-px w-7 bg-white/25" />
          <div className="absolute left-0 top-[62.5%] h-px w-7 bg-white/25" />
          <div className="absolute left-0 top-[87.5%] h-px w-7 bg-white/25" />
          <div className="absolute left-7 top-[12.5%] h-[75%] w-px bg-white/25" />
          <div className="absolute left-7 top-1/2 h-px w-12 bg-steel-light" />
          <div className="absolute right-0 top-1/2 h-1.5 w-1.5 -translate-y-1/2 rotate-45 border-r border-t border-steel-light" />
        </div>

        <Surface accent className="flex flex-1 items-center px-5 py-7">
          <div>
            <p className="font-display text-lg font-medium tracking-tight text-bone">Your website</p>
            <p className="mt-1.5 text-[13px] leading-relaxed text-silver">
              One destination, one set of numbers, one place the enquiry is captured.
            </p>
          </div>
        </Surface>
      </div>
    </Stage>
  );
}

/* ----------------------------------------------------------- 03 CONVERT */

function ConvertVisual() {
  return (
    <Stage
      label="Diagram: a landing page visit becoming an action and a captured, attributed enquiry"
      caption="Visit → action → attributed enquiry"
    >
      <div className="mx-auto w-full max-w-md">
        <Browser url="yourcompany.com/roof-replacement">
          <Page cta="Get my quote" />
        </Browser>
        <Flow label="Calls or completes the form" />
        <Outcome title="Enquiry captured" meta={['Source', 'Action', 'Timing']} />
      </div>
    </Stage>
  );
}

/* ----------------------------------------------------------- 04 QUALIFY */

function QualifyVisual() {
  const checks = ['Service you actually offer', 'Inside your service area', 'Real, current intent'];
  return (
    <Stage
      label="Diagram: an enquiry checked against service, area and intent, then routed and scheduled"
      caption="Checked → routed → scheduled"
    >
      <div className="mx-auto w-full max-w-md">
        <Step rank="loud">New enquiry</Step>
        <Flow label="Checked against" />
        <Surface className="px-4 py-3.5">
          <ul className="space-y-2">
            {checks.map((c) => (
              <li key={c} className="flex items-center gap-2.5">
                <Check className="h-3 w-3 shrink-0 text-steel-light" strokeWidth={3} aria-hidden="true" />
                <span className="text-[12.5px] text-bone">{c}</span>
              </li>
            ))}
          </ul>
          <p className="mt-3 border-t border-white/10 pt-2.5 text-[11px] leading-snug text-silver/70">
            Anything that fails is not passed on as a job.
          </p>
        </Surface>
        <Flow label="Routed" />
        <Outcome title="Booked with the right person" meta={['Trade', 'Area', 'Time']} />
      </div>
    </Stage>
  );
}

/* --------------------------------------------------------- 05 FOLLOW UP */

function FollowUpVisual() {
  const touches = [
    { icon: Clock, t: 'Minutes', d: 'Automatic first reply' },
    { icon: MessageSquare, t: 'Day 1–3', d: 'Email and SMS follow-up' },
    { icon: CalendarCheck, t: 'Before the visit', d: 'Appointment reminder' },
  ];
  return (
    <Stage
      label="Diagram: an enquiry followed up automatically within minutes, over days, and before the appointment"
      caption="Answered before it goes cold"
    >
      <div className="mx-auto w-full max-w-md">
        <ol className="relative">
          <span className="absolute bottom-3 left-[13px] top-3 w-px bg-white/15" aria-hidden="true" />
          {touches.map((x) => (
            <li key={x.t} className="relative flex gap-3.5 pb-3 last:pb-0">
              <Surface className="grid h-[27px] w-[27px] shrink-0 place-items-center rounded-full">
                <x.icon className="h-3.5 w-3.5 text-steel-light" strokeWidth={2} aria-hidden="true" />
              </Surface>
              <div className="pt-1">
                <p className="text-[12.5px] font-bold leading-none text-bone">{x.t}</p>
                <p className="mt-1 text-[11px] leading-snug text-silver/60">{x.d}</p>
              </div>
            </li>
          ))}
        </ol>
        <div className="mt-3">
          <Outcome title="Still a live conversation" />
        </div>
      </div>
    </Stage>
  );
}

/* -------------------------------------------------------------- 06 GROW */

const CYCLE = [
  { t: 'Qualified opportunity', d: 'A real job, checked.' },
  { t: 'Follow-up', d: 'Contacted while it is warm.' },
  { t: 'Data', d: 'What produced it, and what it cost.' },
  { t: 'Optimisation', d: 'Spend moves to what worked.' },
  { t: 'Better campaign', d: 'The next cycle starts stronger.' },
  { t: 'Better conversion', d: 'More of the same traffic converts.' },
];

function GrowVisual() {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.4 });
  const [active, setActive] = useState(0);

  // The indicator walks the loop only while it is on screen, so the page is not
  // running a timer for a section nobody is looking at.
  useEffect(() => {
    if (reduce || !inView) return undefined;
    const id = setInterval(() => setActive((i) => (i + 1) % CYCLE.length), 1600);
    return () => clearInterval(id);
  }, [reduce, inView]);

  return (
    <div ref={ref}>
      <Stage
        label="Diagram: a loop in which follow-up and data feed optimisation, producing more qualified opportunities"
        caption="Every cycle feeds the next"
      >
        {/* Desktop: a track, so each label owns a column and cannot collide. */}
        <div className="hidden lg:block">
          <ol className="grid grid-cols-6 gap-3">
            {CYCLE.map((s, i) => {
              const on = i === active;
              return (
                <li key={s.t} data-testid={`grow-stop-${i}`} aria-current={on ? 'step' : undefined}>
                  <span
                    className={`block h-px w-full transition-colors duration-500 ${on ? 'bg-steel-light' : 'bg-white/15'}`}
                    aria-hidden="true"
                  />
                  <span className="relative block">
                    <span
                      className={`absolute -top-[3.5px] left-0 block h-1.5 w-1.5 rounded-full transition-all duration-500 ${
                        on ? 'scale-150 bg-steel-light' : 'bg-white/25'
                      }`}
                      aria-hidden="true"
                    />
                  </span>
                  <p
                    className={`mt-4 font-display text-[13.5px] font-medium leading-snug tracking-tight transition-colors duration-500 ${
                      on ? 'text-bone' : 'text-bone/55'
                    }`}
                  >
                    {s.t}
                  </p>
                  <p
                    className={`mt-1 text-[11px] leading-snug transition-colors duration-500 ${
                      on ? 'text-silver' : 'text-silver/65'
                    }`}
                  >
                    {s.d}
                  </p>
                </li>
              );
            })}
          </ol>

          {/* The return path: the last stage feeds the first. */}
          <div className="relative mt-8 h-12" aria-hidden="true">
            <div className="absolute inset-x-0 bottom-0 h-full rounded-b-2xl border-b border-l border-r border-white/20" />
            <span className="absolute -top-[5px] left-0 h-2 w-2 -translate-x-1/2 rotate-45 border-l border-t border-steel-light" />
          </div>
          <p className="-mt-4 text-center text-[11px] text-silver/60">
            More qualified opportunities, from the same system
          </p>
        </div>

        {/* Mobile: a vertical spine, composed separately. */}
        <ol className="relative border-l border-white/20 pl-5 lg:hidden">
          {CYCLE.map((s) => (
            <li key={s.t} className="relative pb-5 last:pb-0">
              <span className="absolute -left-[1.42rem] top-1.5 h-1.5 w-1.5 rounded-full bg-steel-light" aria-hidden="true" />
              <p className="text-[13px] font-bold leading-none text-bone">{s.t}</p>
              <p className="mt-1 text-[11.5px] leading-snug text-silver/65">{s.d}</p>
            </li>
          ))}
          <li className="relative pt-1">
            <span className="absolute -left-[1.42rem] top-2 h-2 w-2 -translate-x-[1px] rotate-45 border-l border-t border-steel-light" aria-hidden="true" />
            <p className="text-[11.5px] text-silver/60">More qualified opportunities, from the same system</p>
          </li>
        </ol>
      </Stage>
    </div>
  );
}

const VISUALS = {
  build: BuildVisual,
  'get-found': GetFoundVisual,
  attract: AttractVisual,
  convert: ConvertVisual,
  qualify: QualifyVisual,
  'follow-up': FollowUpVisual,
  grow: GrowVisual,
};

function StagePanel({ stage }) {
  const Visual = VISUALS[stage.id];
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-[0.22em] text-silver">Stage {stage.index}</p>
      <div className="mt-4 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between xl:gap-12">
        <h3 className="max-w-xl font-display text-3xl sm:text-4xl lg:text-[2.6rem] font-medium tracking-tight leading-[1.08] text-bone">
          {stage.headline}
        </h3>
        <p className="max-w-md text-[17px] leading-relaxed text-silver xl:text-right">{stage.description}</p>
      </div>

      <div className="mt-9 border border-white/10 bg-white/[0.02] px-6 py-9 sm:px-10 sm:py-11">
        <Visual />
      </div>

      <ul className="mt-8 grid grid-cols-1 gap-x-10 gap-y-5 sm:grid-cols-2">
        {stage.services.map((s) => (
          <li key={s.name} className="border-t border-white/12 pt-3.5">
            <p className="text-[15px] font-bold text-bone">{s.name}</p>
            <p className="mt-1 text-[13px] leading-relaxed text-silver/75">{s.note}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function GrowthEngine() {
  const [active, setActive] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(0);
  const reduce = useReducedMotion();
  const stage = STAGES[active];

  return (
    <div data-testid="growth-engine">
      <Reveal>
        <p className="overline-tag text-silver">
          <span className="h-px w-8 bg-steel-light" aria-hidden="true" />
          The HyenaMind growth engine
        </p>
        <h2 className="mt-6 max-w-4xl font-display text-[2.3rem] sm:text-5xl lg:text-[4.5rem] tracking-tighter leading-[1.02] font-medium text-bone">
          One system. <span className="text-steel-light">Every stage of growth.</span>
        </h2>
      </Reveal>

      <div className="mt-14 hidden lg:grid lg:grid-cols-12 gap-12 xl:gap-16 items-start">
        <div className="lg:col-span-4">
          <div role="tablist" aria-label="Growth engine stages">
            {STAGES.map((s, i) => {
              const on = i === active;
              return (
                <button
                  key={s.id}
                  role="tab"
                  aria-selected={on}
                  data-testid={`engine-stage-${s.id}`}
                  onClick={() => setActive(i)}
                  className={`group relative block w-full border-t py-5 pl-5 text-left transition-colors duration-300 ${
                    on ? 'border-t-steel-light' : 'border-t-white/12 hover:border-t-white/35'
                  } ${i === STAGES.length - 1 ? 'border-b border-b-white/12' : ''}`}
                >
                  <span className="flex items-baseline gap-5">
                    <span
                      className={`font-display text-sm font-semibold tracking-[0.18em] transition-colors ${
                        on ? 'text-silver' : 'text-bone/50'
                      }`}
                    >
                      {s.index}
                    </span>
                    <span
                      className={`font-display text-2xl xl:text-3xl font-medium tracking-tight transition-colors ${
                        on ? 'text-bone' : 'text-bone/50 group-hover:text-bone/80'
                      }`}
                    >
                      {s.title}
                    </span>
                  </span>
                  {on && (
                    <motion.span
                      layoutId="engine-marker"
                      className="absolute left-0 top-0 h-full w-0.5 bg-steel-light"
                      aria-hidden="true"
                    />
                  )}
                </button>
              );
            })}
          </div>

          <p className="mt-9 max-w-xs pl-5 text-[15px] leading-relaxed text-silver/75">
            Each stage works on its own. They work considerably better when one company runs all seven.
          </p>
          <Link
            to="/contractors/grow-your-business"
            data-testid="engine-cta"
            className="group ml-5 mt-6 inline-flex items-center gap-2.5 border-b-2 border-white/20 py-2 text-[15px] font-bold text-bone transition-colors hover:border-steel-light hover:text-steel-light"
          >
            Build my growth system
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>

        <div className="lg:col-span-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={stage.id}
              initial={reduce ? { opacity: 0 } : { opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduce ? { opacity: 0 } : { opacity: 0, y: -14 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              data-testid={`engine-panel-${stage.id}`}
            >
              <StagePanel stage={stage} />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Mobile: an accordion, so six stages stay scannable. */}
      <div className="mt-12 lg:hidden">
        {STAGES.map((s, i) => {
          const Visual = VISUALS[s.id];
          const on = i === mobileOpen;
          return (
            <div
              key={s.id}
              className={`border-t border-white/12 ${i === STAGES.length - 1 ? 'border-b' : ''}`}
              data-testid={`engine-mobile-${s.id}`}
            >
              <h3>
                <button
                  onClick={() => setMobileOpen(on ? -1 : i)}
                  aria-expanded={on}
                  aria-controls={`engine-mobile-panel-${s.id}`}
                  data-testid={`engine-mobile-toggle-${s.id}`}
                  className="flex w-full items-center gap-4 py-5 text-left"
                >
                  <span className="font-display text-sm font-semibold tracking-[0.18em] text-silver">{s.index}</span>
                  <span className="flex-1 font-display text-xl font-medium tracking-tight text-bone">{s.title}</span>
                  <ChevronDown
                    className={`h-5 w-5 shrink-0 text-silver transition-transform duration-300 ${on ? 'rotate-180' : ''}`}
                    aria-hidden="true"
                  />
                </button>
              </h3>
              <div id={`engine-mobile-panel-${s.id}`} hidden={!on}>
                <p className="text-[15px] leading-relaxed text-silver">{s.description}</p>
                <div className="mt-5 border border-white/10 bg-white/[0.02] px-5 py-7">
                  <Visual />
                </div>
                <ul className="mt-5 space-y-3 pb-7">
                  {s.services.map((sv) => (
                    <li key={sv.name} className="border-t border-white/12 pt-3">
                      <p className="text-[14px] font-bold text-bone">{sv.name}</p>
                      <p className="mt-0.5 text-[12.5px] leading-relaxed text-silver/75">{sv.note}</p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}

        <Link
          to="/contractors/grow-your-business"
          className="group mt-8 inline-flex items-center gap-2.5 border-b-2 border-white/20 py-2 text-[15px] font-bold text-bone transition-colors hover:border-steel-light hover:text-steel-light"
        >
          Build my growth system
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </div>
    </div>
  );
}
